import React from 'react';
import './ContentState.css';

const SUPPORTED_KINDS = new Set(['loading', 'empty', 'error']);

/**
 * Shared feedback surface for content that is loading, unavailable, or empty.
 * `action` accepts any React node, allowing callers to supply a Link or button.
 */
function ContentState({
  kind = 'empty',
  eyebrow,
  title,
  message,
  action,
  className = '',
}) {
  const safeKind = SUPPORTED_KINDS.has(kind) ? kind : 'empty';
  const stateClassName = [
    'insights-content-state',
    `insights-content-state--${safeKind}`,
    className,
  ].filter(Boolean).join(' ');

  return (
    <section
      aria-busy={safeKind === 'loading' ? 'true' : undefined}
      aria-live={safeKind === 'error' ? 'assertive' : 'polite'}
      className={stateClassName}
      role={safeKind === 'error' ? 'alert' : 'status'}
    >
      {safeKind === 'loading' && (
        <span aria-hidden="true" className="insights-content-state__loader" />
      )}
      {eyebrow && (
        <p className="insights-content-state__eyebrow">{eyebrow}</p>
      )}
      {title && <h2 className="insights-content-state__title">{title}</h2>}
      {message && (
        <p className="insights-content-state__message">{message}</p>
      )}
      {action && (
        <div className="insights-content-state__action">{action}</div>
      )}
    </section>
  );
}

function LoadingState({
  title = 'Loading insights',
  message = 'Gathering the latest writing.',
  ...props
}) {
  return (
    <ContentState
      {...props}
      kind="loading"
      message={message}
      title={title}
    />
  );
}

function EmptyState({
  title = 'No insights yet',
  message = 'New writing will appear here when it is published.',
  ...props
}) {
  return (
    <ContentState
      {...props}
      kind="empty"
      message={message}
      title={title}
    />
  );
}

function ErrorState({
  title = 'Insights are unavailable',
  message = 'Please try again in a moment.',
  ...props
}) {
  return (
    <ContentState
      {...props}
      kind="error"
      message={message}
      title={title}
    />
  );
}

export { LoadingState, EmptyState, ErrorState };
export default ContentState;
